import winston from 'winston';
import chalk from 'chalk';
import { IssueError, FileNotFoundError, InvalidFilenameError, InvalidTargetError, FileExistsError, handleError } from '../errors/errors.js';

// Define our custom logger interface
export interface CustomLogger extends winston.Logger {
  success(message: any, ...meta: any[]): winston.Logger;
  debug(message: any, ...meta: any[]): winston.Logger;
}

const { format, createLogger, transports } = winston;
const { combine, timestamp, printf } = format;

// Custom format for different log levels
const customFormat = printf((info: winston.Logform.TransformableInfo) => {
  const ts = new Date(info.timestamp?.toString() || Date.now()).toLocaleTimeString();
  
  switch (info.level) {
    case 'error':
      return `${chalk.gray(ts)} ${chalk.red('✖')} ${chalk.red(info.message)}`;
    case 'warn':
      return `${chalk.gray(ts)} ${chalk.yellow('⚠')} ${chalk.yellow(info.message)}`;
    case 'info':
      return `${chalk.gray(ts)} ${chalk.blue('ℹ')} ${info.message}`;
    case 'debug':
      return `${chalk.gray(ts)} ${chalk.gray('🔍')} ${chalk.gray(info.message)}`;
    case 'success':
      return `${chalk.gray(ts)} ${chalk.green('✔')} ${chalk.green(info.message)}`;
    default:
      return `${chalk.gray(ts)} ${info.message}`;
  }
});

// Create custom success level
const customLevels = {
  levels: {
    error: 0,
    warn: 1,
    info: 2,
    debug: 3,
    success: 4
  },
  colors: {
    error: 'red',
    warn: 'yellow',
    info: 'blue',
    debug: 'gray',
    success: 'green'
  }
};

// Create a filter to completely silence debug messages unless explicitly enabled
const silenceDebug = format((info) => {
  if (info.level === 'debug' && !process.env.DEBUG_ENABLED) {
    return false;
  }
  return info;
});

// Create the logger with silent debug by default
const baseLogger = createLogger({
  levels: customLevels.levels,
  format: combine(
    silenceDebug(),
    timestamp(),
    customFormat
  ),
  transports: [
    new transports.Console({
      level: 'info'
    })
  ]
});

// Add success method to match our interface
(baseLogger as any).success = baseLogger.info;

export const logger = baseLogger as CustomLogger;

// Add colors to winston
winston.addColors(customLevels.colors);

// Function to configure logger based on debug flag
export function configureLogger(debug: boolean = false) {
  process.env.DEBUG_ENABLED = debug ? 'true' : '';
  const level = debug ? 'debug' : 'info';
  logger.transports.forEach(transport => {
    if (transport instanceof winston.transports.Console) {
      transport.level = level;
    }
  });
}

// Re-export error types for convenience
export { IssueError, FileNotFoundError, InvalidFilenameError, InvalidTargetError, FileExistsError, handleError }; 